/** P2P helpers — mirrors mobile PostedRequestCard / p2pHelpers logic for web. */

import { API_BASE_URL } from "../../config";
import { formatAppDate, formatAppDateTime, formatAppTime } from "./appTimeFormat";
import { sortLeaguesByPriority } from "./leaguePriority";

/** Deep-link / share URL for a P2P duel (OG preview → web app). */
export const buildP2PShareLink = (p2pId, origin = "") => {
  const id = String(p2pId || "").trim();
  if (!id) return "";
  const apiBase = String(API_BASE_URL || "").replace(/\/$/, "");
  if (apiBase) {
    return `${apiBase}/share/p2p/${encodeURIComponent(id)}`;
  }
  const base =
    origin || (typeof window !== "undefined" ? window.location.origin : "");
  if (!base) return "";
  return `${base}/app/p2p?w=${encodeURIComponent(id)}`;
};

/** Short message for native share / clipboard. */
export const buildP2PShareText = (bet) => {
  if (!bet) return "Check out this P2P duel on BetBond";
  const fixture = bet.fixture || {};
  const home = fixture.teams?.home?.name || "Home";
  const away = fixture.teams?.away?.name || "Away";
  const owner =
    typeof bet.user === "object" && bet.user?.name
      ? bet.user.name
      : "Someone";
  const stake = Number(bet.amount || 0).toLocaleString();
  return `${owner} opened a P2P on ${home} vs ${away} · ${stake} cash. Bet against them on BetBond:`;
};

/** Shared P2P vocabulary — creator backs, joiner bets against. */
export const P2P_TERMS = {
  creatorLabel: "Creator backs",
  backingTag: "Backing",
  againstTag: "Against",
  betAgainst: "Bet against",
  betAgainstStake: (amount) =>
    `Bet against · min stake ${Number(amount || 0).toLocaleString()} cash`,
  loginToBetAgainst: "Log in to bet against",
  cantBetAgainstNow: (label) => `${label} — you can't bet against now.`,
  rosterAgainst: "Against",
  rosterRoleAgainst: "Against",
  rosterRoleInvite: "Accepted invite",
  noAgainstYet: (name) =>
    `No one against yet. Be first to bet against ${name}.`,
  yourBacking: "Your backing",
  creatorBacking: "Creator backing",
  waitingForAgainst: "Waiting for someone on the against side.",
  anythingButBacking: "Anything but their backing",
  confirmAgainst: "Confirm against",
  acceptAndAgainst: "Accept & bet against",
};

export const P2P_FILTERS = [
  "All",
  "Active",
  "Pending",
  "Settled",
  "Voided",
  "Created",
  "Joined",
  "Invited",
  "Following",
  "Favorites",
];

export const AUTH_FILTERS = new Set([
  "Created",
  "Joined",
  "Invited",
  "Following",
  "Favorites",
]);

export const sameUserId = (a, b) => {
  if (!a || !b) return false;
  const idA = typeof a === "object" ? a._id : a;
  const idB = typeof b === "object" ? b._id : b;
  return idA?.toString() === idB?.toString();
};

export const getOwner = (bet) =>
  typeof bet.user === "object" ? bet.user : { _id: bet.user, name: "Creator" };

/** Spots claimed by one joiner / invite row (legacy rows count as 1). */
export const spotsFilledOfEntry = (entry) => {
  const n = Number(entry?.spotsFilled);
  if (Number.isFinite(n) && n > 0) return Math.floor(n);
  return 1;
};

/** Seats with money in (public joiners + accepted invitees). */
export const getFilledSlots = (bet) => {
  const owner = getOwner(bet);
  let used = 0;
  for (const row of bet?.peopleJoined || []) {
    const uid = typeof row.user === "object" ? row.user?._id : row.user;
    if (sameUserId(uid, owner._id)) continue;
    used += spotsFilledOfEntry(row);
  }
  for (const inv of bet?.invites || []) {
    if (inv.status !== "Accepted") continue;
    if (sameUserId(inv.user, owner._id)) continue;
    used += spotsFilledOfEntry(inv);
  }
  return used;
};

/** Seats reserved exclusively for pending invitees. */
export const getReservedPendingSlots = (bet) => {
  let reserved = 0;
  for (const inv of bet?.invites || []) {
    if (inv.status === "Pending") reserved += spotsFilledOfEntry(inv);
  }
  return reserved;
};

/** Total against-side capacity occupied (filled + pending invite reservations). */
export const getUsedSlots = (bet) => {
  return getFilledSlots(bet);
};

/** Alias kept for existing call sites — now returns occupied capacity, not headcount. */
export const getJoinedCount = (bet) => getUsedSlots(bet);

/** How many spots the given user already fills on this offer (0 if none). */
export const getUserSpotsFilled = (bet, userId) => {
  if (!userId) return 0;
  for (const row of bet?.peopleJoined || []) {
    if (sameUserId(row.user, userId)) return spotsFilledOfEntry(row);
  }
  for (const inv of bet?.invites || []) {
    if (inv.status !== "Accepted") continue;
    if (sameUserId(inv.user, userId)) return spotsFilledOfEntry(inv);
  }
  return 0;
};

/** Counters / joins close this long before kickoff (matches canJoin). */
export const P2P_JOIN_BUFFER_MS = 5 * 60 * 1000;

export const calculateTimeRemaining = (gameTime) => {
  if (!gameTime) return "";
  const closure = new Date(new Date(gameTime).getTime() - P2P_JOIN_BUFFER_MS);
  const diff = closure - Date.now();
  if (diff <= 0) return "";

  const days = Math.floor(diff / (1000 * 60 * 60 * 24));
  const hours = Math.floor((diff / (1000 * 60 * 60)) % 24);
  const minutes = Math.floor((diff / (1000 * 60)) % 60);
  const seconds = Math.floor((diff / 1000) % 60);

  let s = "";
  if (days > 0) s += `${days}d `;
  if (hours > 0 || days > 0) s += `${hours}h `;
  if (minutes > 0 || hours > 0 || days > 0) s += `${minutes}m `;
  s += `${seconds}s`;
  return s.trim();
};

/** Join window closes 5m before kickoff — progress from post time → closure. */
export const calculateClosureProgress = (gameTime, createdAt) => {
  if (!gameTime) return 0;

  const closureMs = new Date(gameTime).getTime() - P2P_JOIN_BUFFER_MS;
  const now = Date.now();
  if (now >= closureMs) return 100;

  const postedMs = createdAt ? new Date(createdAt).getTime() : NaN;
  const fallbackStart = closureMs - 7 * 24 * 60 * 60 * 1000;
  const startMs = Number.isFinite(postedMs) ? postedMs : fallbackStart;
  const start = Math.min(startMs, closureMs - 60 * 1000);

  const total = closureMs - start;
  if (total <= 0) return 0;

  const elapsed = now - start;
  return Math.min(100, Math.max(0, Math.round((elapsed / total) * 100)));
};

export const formatOutcome = (agreedOdds, fixture) => {
  if (!agreedOdds) return "—";
  const { section, subSection, key } = agreedOdds;
  const home = fixture?.teams?.home?.name || "Home";
  const away = fixture?.teams?.away?.name || "Away";

  const sectionLabels = {
    oneXTwo: "",
    doubleChance: "Double Chance",
    bothTeamsToScore: "Both Teams to Score",
    overUnderGoals: "Over/Under Goals",
    correctScore: "Correct Score",
    cleanSheet: "Clean Sheet",
    oddEven: "Odd/Even",
    teamToScore: "Team to Score",
    winToNil: "Win to Nil",
    drawNoBet: "Draw No Bet",
    doubleChanceAndBothTeamsToScore: "Double Chance & BTTS",
  };

  const subSectionLabels = {
    fullTime: "Full Time",
    halfTime: "First Half",
    secondHalf: "Second Half",
    homeTeam: home,
    awayTeam: away,
  };

  const keyLabels = {
    1: home,
    X: "Draw",
    2: away,
    "1X": `${home} or Draw`,
    X2: `Draw or ${away}`,
    12: `${home} or ${away}`,
    homeTeam: home,
    awayTeam: away,
    bothTeams: "Both Teams",
    noGoal: "No Goal",
    yes: "Yes",
    no: "No",
    odd: "Odd",
    even: "Even",
    over1_5: "Over 1.5",
    under1_5: "Under 1.5",
    over2_5: "Over 2.5",
    under2_5: "Under 2.5",
    over3_5: "Over 3.5",
    under3_5: "Under 3.5",
  };

  const sectionLabel = sectionLabels[section] || section;
  const subLabel = subSectionLabels[subSection] || subSection;
  const keyLabel = keyLabels[key] || key;

  if (["fullTime", "halfTime", "secondHalf"].includes(subSection)) {
    return sectionLabel
      ? `${sectionLabel} · ${subLabel}: ${keyLabel}`
      : `${subLabel}: ${keyLabel}`;
  }
  if (subSection === "homeTeam" || subSection === "awayTeam") {
    return sectionLabel
      ? `${sectionLabel} · ${subLabel}: ${keyLabel}`
      : `${subLabel}: ${keyLabel}`;
  }
  return sectionLabel ? `${sectionLabel}: ${keyLabel}` : keyLabel;
};

export const teamAbbrev = (name) =>
  (name || "?")
    .replace(/^(FC|AC|SC|CF)\s+/i, "")
    .replace(/[^a-zA-Z0-9]/g, "")
    .slice(0, 3)
    .toUpperCase() || "?";

const scopeLabel = (subSection, home, away) => {
  const map = {
    fullTime: "Full Time",
    halfTime: "1st Half",
    secondHalf: "2nd Half",
    homeTeam: home,
    awayTeam: away,
  };
  return map[subSection] || "";
};

/** Structured outcome for the Creator backs UI. */
export const parseOutcomeParts = (agreedOdds, fixture) => {
  if (!agreedOdds) return null;

  const { section, subSection, key } = agreedOdds;
  const home = fixture?.teams?.home?.name || "Home";
  const away = fixture?.teams?.away?.name || "Away";
  const scope = scopeLabel(subSection, home, away);
  const fullText = formatOutcome(agreedOdds, fixture);

  const keyLabels = {
    1: home,
    X: "Draw",
    2: away,
    "1X": "1X",
    X2: "X2",
    12: "12",
    yes: "Yes",
    no: "No",
    odd: "Odd",
    even: "Even",
    over1_5: "Over 1.5",
    under1_5: "Under 1.5",
    over2_5: "Over 2.5",
    under2_5: "Under 2.5",
    over3_5: "Over 3.5",
    under3_5: "Under 3.5",
  };

  const sectionBadge = {
    oneXTwo: scope || "Match result",
    doubleChance: "Double Chance",
    bothTeamsToScore: "BTTS",
    overUnderGoals: "Over / Under",
    correctScore: "Correct Score",
    cleanSheet: "Clean Sheet",
    oddEven: "Odd / Even",
    teamToScore: "Team to Score",
    winToNil: "Win to Nil",
    drawNoBet: "Draw No Bet",
    doubleChanceAndBothTeamsToScore: "DC & BTTS",
  };

  const badge =
    scope && section !== "oneXTwo"
      ? `${sectionBadge[section] || section} · ${scope}`
      : sectionBadge[section] || scope || "Market";

  const pickLabel = keyLabels[key] || key || fullText;
  const base = { key, badge, pickLabel, fullText };

  if (
    section === "oneXTwo" &&
    ["fullTime", "halfTime", "secondHalf"].includes(subSection)
  ) {
    return {
      ...base,
      visual: "1x2",
      options: [
        { id: "1", label: home, tag: "Home", hint: `${home} to win` },
        { id: "X", label: "Draw", hint: "Draw" },
        { id: "2", label: away, tag: "Away", hint: `${away} to win` },
      ],
    };
  }

  if (
    section === "doubleChance" &&
    ["fullTime", "halfTime", "secondHalf"].includes(subSection)
  ) {
    return {
      ...base,
      visual: "triple",
      options: [
        {
          id: "1X",
          label: "1X",
          sublabel: `${home} or Draw`,
          hint: `${home} or Draw`,
        },
        {
          id: "12",
          label: "12",
          sublabel: `${home} or ${away}`,
          hint: `${home} or ${away}`,
        },
        {
          id: "X2",
          label: "X2",
          sublabel: `Draw or ${away}`,
          hint: `Draw or ${away}`,
        },
      ],
    };
  }

  if (section === "bothTeamsToScore") {
    return {
      ...base,
      visual: "binary",
      options: [
        { id: "yes", label: "Yes", hint: "Both score" },
        { id: "no", label: "No", hint: "Not both" },
      ],
    };
  }

  if (section === "oddEven") {
    return {
      ...base,
      visual: "binary",
      options: [
        { id: "odd", label: "Odd", hint: "Odd total" },
        { id: "even", label: "Even", hint: "Even total" },
      ],
    };
  }

  if (section === "overUnderGoals" && key?.includes("_")) {
    const line = key.replace(/^(over|under)/, "").replace("_", ".");
    const overKey = `over${key.match(/(\d_\d)/)?.[1] || ""}`;
    const underKey = `under${key.match(/(\d_\d)/)?.[1] || ""}`;
    return {
      ...base,
      visual: "binary",
      options: [
        { id: overKey, label: `Over ${line}`, hint: "Over line" },
        { id: underKey, label: `Under ${line}`, hint: "Under line" },
      ],
    };
  }

  return { ...base, visual: "text" };
};

/** Label for the against side — prefers short sublabel/label over verbose hints. */
export const formatCounterSideLabel = (options) => {
  if (!options?.length) return "Against";
  const piece = (o) => o.sublabel || o.label || o.hint;
  const parts = options.map(piece).filter(Boolean);
  if (parts.length === 1) return parts[0];
  if (parts.length === 2) return `${parts[0]} or ${parts[1]}`;
  return `${parts.slice(0, -1).join(", ")}, or ${parts[parts.length - 1]}`;
};

function sameCalendarDay(a, b) {
  return a.toDateString() === b.toDateString();
}

export const getP2PFixtureDateKey = (bet) => {
  const raw = bet?.fixture?.date || bet?.gameTime;
  if (!raw) return "unknown";
  const d = new Date(raw);
  if (Number.isNaN(d.getTime())) return "unknown";
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};

export const formatP2PDateSectionTitle = (dateKey) => {
  if (dateKey === "unknown") return "Date TBD";

  const d = new Date(`${dateKey}T12:00:00`);
  const today = new Date();
  const tomorrow = new Date();
  tomorrow.setDate(today.getDate() + 1);
  const yesterday = new Date();
  yesterday.setDate(today.getDate() - 1);

  if (sameCalendarDay(d, today)) return "Today";
  if (sameCalendarDay(d, tomorrow)) return "Tomorrow";
  if (sameCalendarDay(d, yesterday)) return "Yesterday";

  return formatAppDate(d);
};

const p2pCreatedAtMs = (bet) =>
  new Date(bet?.createdAt || bet?.updatedAt || 0).getTime();

export const groupP2PBetsByFixtureDate = (bets, sortId = "recent") => {
  const bucket = new Map();
  const insertionOrder = [];

  for (const bet of bets || []) {
    const key = getP2PFixtureDateKey(bet);
    if (!bucket.has(key)) {
      bucket.set(key, []);
      insertionOrder.push(key);
    }
    bucket.get(key).push(bet);
  }

  const kickoffMs = (bet) =>
    new Date(bet?.fixture?.date || bet?.gameTime || 0).getTime();

  if (sortId === "recent") {
    const dateKeys = [...insertionOrder].sort((a, b) => {
      const maxA = Math.max(...(bucket.get(a) || []).map(p2pCreatedAtMs), 0);
      const maxB = Math.max(...(bucket.get(b) || []).map(p2pCreatedAtMs), 0);
      return maxB - maxA;
    });

    return dateKeys.map((dateKey) => ({
      dateKey,
      title: formatP2PDateSectionTitle(dateKey),
      bets: bucket.get(dateKey) || [],
    }));
  }

  const dateKeys = Array.from(bucket.keys()).sort((a, b) => {
    if (a === "unknown") return 1;
    if (b === "unknown") return -1;
    return a.localeCompare(b);
  });

  return dateKeys.map((dateKey) => ({
    dateKey,
    title: formatP2PDateSectionTitle(dateKey),
    bets: (bucket.get(dateKey) || []).sort((a, b) => kickoffMs(a) - kickoffMs(b)),
  }));
};

export const statusTone = (status) => {
  const s = (status || "").toLowerCase();
  if (s === "active") return "active";
  if (s === "pending") return "pending";
  if (s === "settled") return "settled";
  if (s === "voided") return "voided";
  return "default";
};

export const fixtureCentre = (fixture) => {
  const short = fixture?.status?.short;
  const goals = fixture?.goals || fixture?.score?.fulltime || {};
  const home = goals.home ?? goals.homeTeam;
  const away = goals.away ?? goals.awayTeam;

  if (short === "FT" || short === "AET" || short === "PEN") {
    return { main: `${home ?? 0} – ${away ?? 0}`, sub: "Full time" };
  }
  if (short && short !== "NS" && short !== "TBD") {
    return { main: short, sub: "Live" };
  }
  if (fixture?.date) {
    const d = new Date(fixture.date);
    return {
      main: formatAppTime(d),
      sub: formatAppDate(d),
    };
  }
  return { main: "TBD", sub: "Kickoff" };
};

export const matchesP2PFilter = (bet, filter, userId, options = {}) => {
  if (filter === "All") return true;
  const f = filter.toLowerCase();
  const { followingIds, favoriteTeam } = options;

  if (["active", "pending", "settled", "voided"].includes(f)) {
    return bet.status?.toLowerCase() === f;
  }
  if (!userId) return false;

  if (f === "created") {
    return sameUserId(bet.user, userId);
  }
  if (f === "joined") {
    const inJoined = bet.peopleJoined?.some((p) => sameUserId(p.user, userId));
    const accepted = bet.invites?.some(
      (i) => sameUserId(i.user, userId) && i.status === "Accepted"
    );
    return inJoined || accepted;
  }
  if (f === "invited") {
    return bet.invites?.some(
      (i) => sameUserId(i.user, userId) && i.status !== "Rejected"
    );
  }
  if (f === "following") {
    return isBetFromFollowedCreator(bet, followingIds);
  }
  if (f === "favorites") {
    return fixtureHasFavoriteTeam(bet.fixture, favoriteTeam);
  }
  return false;
};

export const getCardAction = (bet, userId) => {
  if (!userId) return { label: P2P_TERMS.loginToBetAgainst, tone: "login" };
  if (sameUserId(bet.user, userId)) {
    if (canCancelOrEdit(bet)) {
      return { label: "Manage", tone: "owner" };
    }
    return { label: "Your wager", tone: "owner" };
  }

  const joined = bet.peopleJoined?.some((p) => sameUserId(p.user, userId));
  const invite = bet.invites?.find((i) => sameUserId(i.user, userId));
  const acceptedInvite = invite?.status === "Accepted";
  const alreadyIn = Boolean(joined || acceptedInvite);

  if (alreadyIn && getRemainingCounterCapacity(bet) >= getMinCounterStake(bet) && canJoin(bet)) {
    return { label: "Add stake", tone: "add-stake" };
  }
  if (joined) return { label: "Joined", tone: "joined" };
  if (acceptedInvite) return { label: "Accepted", tone: "joined" };
  if (invite && invite.status !== "Rejected") {
    const userCapacity = getRemainingCounterCapacity(bet);
    const minStake = getMinCounterStake(bet);
    return {
      label: userCapacity >= minStake ? "Accept invite" : "Full",
      tone: userCapacity >= minStake ? "invited" : "full",
    };
  }

  if (getRemainingCounterCapacity(bet) < getMinCounterStake(bet)) return { label: "Full", tone: "full" };
  if (bet.status !== "Active") return { label: "Closed", tone: "closed" };
  if (!canJoin(bet)) return { label: "Unavailable", tone: "closed" };

  return { label: P2P_TERMS.betAgainst, tone: "join" };
};

export const canCancelP2P = (bet) => {
  if (bet.status !== "Active") return false;
  if (Array.isArray(bet.peopleJoined) && bet.peopleJoined.length > 0) {
    return false;
  }
  if (bet.invites?.some((i) => i.status === "Accepted")) return false;
  return true;
};

/** @deprecated alias — use canCancelP2P */
export const canCancelOrEdit = canCancelP2P;

/** Create/edit closes this long before kickoff (counters use P2P_JOIN_BUFFER_MS). */
export const P2P_CREATE_BUFFER_MS = 10 * 60 * 1000;

/** Owner may increase budget / change odds while offer is open pre-kickoff. */
export const canEditP2P = (bet) => {
  if (bet.status !== "Active") return false;
  if (!bet.fixture?.date) return false;

  const short = (bet.fixture?.status?.short || "").toUpperCase();
  const upcoming = new Set(["NS", "TBD", ""]);
  if (short && !upcoming.has(short)) return false;

  const msUntilStart = new Date(bet.fixture.date).getTime() - Date.now();
  if (msUntilStart < P2P_CREATE_BUFFER_MS) return false;

  return true;
};

export const canJoin = (bet) => {
  if (bet.status !== "Active") return false;
  if (!bet.fixture?.date) return false;
  const msUntilStart = new Date(bet.fixture.date).getTime() - Date.now();
  if (msUntilStart <= P2P_JOIN_BUFFER_MS) return false;
  if (getRemainingCounterCapacity(bet) < getMinCounterStake(bet)) return false;
  if (bet.joinMode === "invite_only") return false;
  return true;
};

export const P2P_SORT_OPTIONS = [
  { id: "recent", label: "Most recent" },
  { id: "kickoff", label: "Soonest kickoff" },
  { id: "stake-high", label: "Highest stake" },
  { id: "stake-low", label: "Lowest stake" },
  { id: "open-capacity", label: "Most open capacity" },
];

export const P2P_STATUS_FILTERS = ["Active", "Pending", "Settled", "Voided"];

export const getP2PStatusCounts = (bets) => {
  const counts = { Active: 0, Pending: 0, Settled: 0, Voided: 0 };
  for (const bet of bets || []) {
    const key = bet.status;
    if (key && counts[key] != null) counts[key] += 1;
  }
  return counts;
};

export const hasOpenCapacity = (bet) =>
  getRemainingCounterCapacity(bet) >= getMinCounterStake(bet) && bet.status === "Active";

/** @deprecated use hasOpenCapacity */
export const hasOpenSpots = hasOpenCapacity;

export const getBudgetFillPercent = (bet) => {
  const budget = getCreatorBudget(bet);
  if (budget <= 0) return 0;
  const remaining = getRemainingBudget(bet);
  const matched = Math.max(0, budget - remaining);
  return Math.min(100, Math.round((matched / budget) * 100));
};

export const getP2PLeagueOptions = (bets, limit = 8) => {
  const map = new Map();
  for (const bet of bets || []) {
    const league = bet.fixture?.league;
    const id = league?._id || league?.id || league?.name;
    if (!id) continue;
    const key = String(id);
    if (!map.has(key)) {
      map.set(key, {
        id: key,
        apiId: league?.id ?? null,
        name: league.name || "League",
        logo: league.logo,
        count: 0,
      });
    }
    map.get(key).count += 1;
  }
  const sorted = sortLeaguesByPriority(Array.from(map.values()));
  return typeof limit === "number" && limit > 0 ? sorted.slice(0, limit) : sorted;
};

export const sortP2PBets = (bets, sortId = "recent") => {
  const list = [...(bets || [])];
  const kickoff = (bet) =>
    new Date(bet?.fixture?.date || bet?.gameTime || 0).getTime();

  switch (sortId) {
    case "stake-high":
      return list.sort((a, b) => (b.amount || 0) - (a.amount || 0));
    case "stake-low":
      return list.sort((a, b) => (a.amount || 0) - (b.amount || 0));
    case "open-slots":
    case "open-capacity":
      return list.sort(
        (a, b) => getRemainingCounterCapacity(b) - getRemainingCounterCapacity(a)
      );
    case "kickoff":
      return list.sort((a, b) => kickoff(a) - kickoff(b));
    case "recent":
    default:
      return list.sort((a, b) => p2pCreatedAtMs(b) - p2pCreatedAtMs(a));
  }
};

export const getFixtureId = (bet) => {
  const f = bet?.fixture;
  if (!f) return null;
  return typeof f === "object" ? f._id?.toString() : String(f);
};

/** Coarse display bucket for a fixture's raw short status code. */
export const getFixtureDisplayStatus = (short) => {
  const s = (short || "").toUpperCase();
  const live = new Set(["1H", "2H", "HT", "ET", "BT", "LIVE", "P", "INT"]);
  const finished = new Set(["FT", "AET", "PEN", "FT_PEN"]);
  const cancelled = new Set(["CANC", "ABD"]);
  const postponed = new Set(["PST"]);
  if (live.has(s)) return "Live";
  if (finished.has(s)) return "Finished";
  if (cancelled.has(s)) return "Cancelled";
  if (postponed.has(s)) return "Postponed";
  return "Upcoming";
};

export const MIN_COUNTER_STAKE = 500;

export const getOfferedOdds = (bet) => {
  const n = Number(bet?.offeredOdds);
  return Number.isFinite(n) && n > 1 ? n : 2;
};

export const getCreatorBudget = (bet) =>
  Number(bet?.creatorBudget ?? bet?.totalBetAmount ?? 0);

export const getMinCounterStake = (bet) => {
  const n = Number(bet?.minStake ?? bet?.amount);
  return Number.isFinite(n) && n >= MIN_COUNTER_STAKE ? n : MIN_COUNTER_STAKE;
};

export const getRemainingBudget = (bet) =>
  Number(bet?.remainingBudget ?? bet?.ownerPendingAmount ?? 0);

export const maxTotalCounterStakeFromBudget = (budget, odds) => {
  const o = Number(odds);
  const b = Number(budget);
  if (!Number.isFinite(o) || o <= 1 || !Number.isFinite(b) || b <= 0) return 0;
  return Math.round((b / (o - 1)) * 100) / 100;
};

export const getRemainingCounterCapacity = (bet) => {
  if (bet?.remainingCounterStakeCapacity != null) {
    return Number(bet.remainingCounterStakeCapacity);
  }
  return maxTotalCounterStakeFromBudget(getRemainingBudget(bet), getOfferedOdds(bet));
};

export const getUserStake = (bet, userId) => {
  if (!userId) return 0;
  for (const row of bet?.peopleJoined || []) {
    if (sameUserId(row.user, userId)) {
      return Number(row.stake ?? row.pendingAmount ?? 0);
    }
  }
  for (const inv of bet?.invites || []) {
    if (inv.status !== "Accepted") continue;
    if (sameUserId(inv.user, userId)) {
      return Number(inv.stake ?? inv.pendingAmount ?? 0);
    }
  }
  return 0;
};

export const getTotalCounterStake = (bet) => {
  let total = 0;
  for (const row of bet?.peopleJoined || []) {
    total += Number(row.stake ?? row.pendingAmount ?? 0);
  }
  for (const inv of bet?.invites || []) {
    if (inv.status === "Accepted") {
      total += Number(inv.stake ?? inv.pendingAmount ?? 0);
    }
  }
  return total;
};

export const counterPayout = (stake, odds) =>
  Math.round(Number(stake) * Number(odds) * 100) / 100;

/** Seats still open (budget-based capacity ÷ min stake). */
export const getSpotsRemaining = (bet) => {
  const minStake = getMinCounterStake(bet);
  const cap = getRemainingCounterCapacity(bet);
  if (cap < minStake) return 0;
  return Math.max(0, Math.floor(cap / minStake));
};

/** Spots this user may take: public open + their own reserved pending invite seat(s). */
export const getSpotsRemainingForUser = (bet, userId) => {
  const publicOpen = getSpotsRemaining(bet);
  if (!userId) return publicOpen;
  const pending = (bet?.invites || []).find(
    (inv) => inv.status === "Pending" && sameUserId(inv.user, userId)
  );
  if (!pending) return publicOpen;
  return publicOpen + spotsFilledOfEntry(pending);
};

/** Cash still open to be matched on the against side of an offer. */
export const getOfferOpenCash = (bet) => {
  if (bet?.status !== "Active") return 0;
  return getRemainingCounterCapacity(bet);
};

/** Cash already matched on an offer — counter stakes plus reserved creator liability. */
export const getOfferMatchedCash = (bet) => {
  const matchedLiability = Math.max(
    0,
    getCreatorBudget(bet) - getRemainingBudget(bet)
  );
  return getTotalCounterStake(bet) + matchedLiability;
};

/**
 * Per-outcome book for one market group on a fixture: where the backing money
 * sits. Aggregates open + matched cash and offer count per picked outcome.
 */
export const buildMarketOutcomeBook = (offers = [], fixture) => {
  const map = new Map();
  for (const offer of offers) {
    const parts = parseOutcomeParts(offer.agreedOdds, fixture);
    const label =
      parts?.pickLabel || parts?.fullText || offer?.agreedOdds?.key || "—";
    if (!map.has(label)) {
      map.set(label, { label, openCash: 0, matchedCash: 0, offerCount: 0 });
    }
    const row = map.get(label);
    row.openCash += getOfferOpenCash(offer);
    row.matchedCash += getOfferMatchedCash(offer);
    row.offerCount += 1;
  }
  return Array.from(map.values()).sort(
    (a, b) => b.openCash - a.openCash || b.matchedCash - a.matchedCash
  );
};

/**
 * Groups offers into a league → fixture board (exchange style), mirroring the
 * mobile P2P board. Each league holds fixture entries; each fixture entry
 * carries its offers plus summary stats (offers, markets, open cash, live).
 */
export const groupP2PBetsByLeagueAndFixture = (bets = []) => {
  const leagueMap = new Map();

  for (const bet of bets) {
    const fixture = bet?.fixture;
    if (!fixture) continue;

    const league = fixture.league || {};
    const leagueId = String(league._id || league.id || league.name || "other");
    const fixtureId = String(fixture._id || fixture.id || "unknown");

    if (!leagueMap.has(leagueId)) {
      leagueMap.set(leagueId, {
        leagueId,
        leagueName: league.name || "Other",
        leagueLogo: league.logo || null,
        fixtureMap: new Map(),
      });
    }

    const leagueEntry = leagueMap.get(leagueId);
    if (!leagueEntry.fixtureMap.has(fixtureId)) {
      leagueEntry.fixtureMap.set(fixtureId, {
        fixtureId,
        fixture,
        offers: [],
      });
    }
    leagueEntry.fixtureMap.get(fixtureId).offers.push(bet);
  }

  const buildFixtureStats = (entry) => {
    const offers = entry.offers;
    const markets = new Set(
      offers.map((o) => o?.agreedOdds?.section).filter(Boolean)
    );
    const openCash = offers.reduce((sum, o) => sum + getOfferOpenCash(o), 0);
    const activeCount = offers.filter((o) => o?.status === "Active").length;
    const openCount = offers.filter(hasOpenSpots).length;
    return {
      offerCount: offers.length,
      marketCount: markets.size,
      openCash,
      activeCount,
      openCount,
      status: getFixtureDisplayStatus(entry.fixture?.status?.short),
      kickoff: entry.fixture?.date || null,
    };
  };

  const kickoffMs = (entry) =>
    new Date(entry.fixture?.date || 0).getTime();

  const sections = Array.from(leagueMap.values()).map((leagueEntry) => {
    const fixtures = Array.from(leagueEntry.fixtureMap.values())
      .map((entry) => ({ ...entry, stats: buildFixtureStats(entry) }))
      .sort((a, b) => {
        const liveDiff =
          (b.stats.status === "Live") - (a.stats.status === "Live");
        if (liveDiff !== 0) return liveDiff;
        return kickoffMs(a) - kickoffMs(b);
      });

    const hasLive = fixtures.some((f) => f.stats.status === "Live");
    const offerCount = fixtures.reduce((n, f) => n + f.stats.offerCount, 0);
    const openCount = fixtures.reduce((n, f) => n + f.stats.openCount, 0);

    return {
      leagueId: leagueEntry.leagueId,
      leagueName: leagueEntry.leagueName,
      leagueLogo: leagueEntry.leagueLogo,
      hasLive,
      offerCount,
      openCount,
      fixtures,
    };
  });

  return sortLeaguesByPriority(
    sections.map((section) => ({
      ...section,
      id: section.leagueId,
      name: section.leagueName,
      count: section.offerCount,
    }))
  ).map(({ id, name, count, ...section }) => section);
};

/** Groups a fixture's offers by market section for the expanded board view. */
export const groupOffersByMarket = (offers = []) => {
  const map = new Map();
  for (const offer of offers) {
    const section = offer?.agreedOdds?.section || "other";
    if (!map.has(section)) map.set(section, []);
    map.get(section).push(offer);
  }
  return Array.from(map.entries()).map(([section, sectionOffers]) => ({
    section,
    offers: sectionOffers,
    openCash: sectionOffers.reduce((s, o) => s + getOfferOpenCash(o), 0),
  }));
};

export const getLiveScoreLine = (fixture) => {
  const short = (fixture?.status?.short || "").toUpperCase();
  const goals = fixture?.goals || fixture?.score?.fulltime || {};
  const home = goals.home ?? goals.homeTeam;
  const away = goals.away ?? goals.awayTeam;
  const liveSet = new Set(["1H", "2H", "HT", "ET", "BT", "LIVE", "P", "INT"]);
  const finSet = new Set(["FT", "AET", "PEN", "FT_PEN"]);
  if (home == null || away == null) return null;
  if (liveSet.has(short) || finSet.has(short)) {
    return {
      score: `${home} – ${away}`,
      label: liveSet.has(short) ? "Live" : "Full time",
    };
  }
  return null;
};

/** Typical counter payout at min stake × offered odds. */
export const getP2PWinPayout = (bet) => {
  const stake = getMinCounterStake(bet);
  const odds = getOfferedOdds(bet);
  const joined = bet.peopleJoined?.find((p) => p.potentialWinnings > 0);
  if (joined?.potentialWinnings) return joined.potentialWinnings;
  return counterPayout(stake, odds);
};

export const getP2PPoolTotal = (bet) => {
  const explicit = Number(bet?.totalBetAmount);
  if (explicit > 0) return explicit;
  const budget = getCreatorBudget(bet);
  const matchedBudget = Math.max(0, budget - getRemainingBudget(bet));
  return budget + matchedBudget;
};

export const getPendingInvites = (bet) =>
  (bet?.invites || []).filter((i) => i.status === "Pending");

export const isHotStance = (bet) => {
  if (bet?.status !== "Active") return false;
  const cap = getRemainingCounterCapacity(bet);
  const minStake = getMinCounterStake(bet);
  return cap > 0 && cap <= minStake * 1.5;
};

export const formatP2PRosterTime = (iso) => {
  if (!iso) return "—";
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return "—";
  const d = new Date(t);
  const now = Date.now();
  const delta = Math.max(0, (now - t) / 1000);
  if (delta < 86400) {
    if (delta < 60) return "just now";
    if (delta < 3600) return `${Math.floor(delta / 60)}m ago`;
    return `${Math.floor(delta / 3600)}h ago`;
  }
  return formatAppDateTime(d);
};

/** Counter-party players only (public join or accepted invite — not the creator). */
export const getP2PRosterEntries = (bet) => {
  const owner = getOwner(bet);
  const seen = new Set();
  const entries = [];

  const push = (id, name, role, at, order) => {
    const key = id != null ? String(id) : name;
    if (seen.has(key)) return;
    seen.add(key);
    entries.push({ id: key, name, role, at, order });
  };

  for (const p of bet.peopleJoined || []) {
    const u = typeof p.user === "object" ? p.user : null;
    const uid = u?._id || p.user;
    if (sameUserId(uid, owner._id)) continue;
    push(uid, u?.name || "Player", P2P_TERMS.rosterRoleAgainst, p.joinedAt, 1);
  }

  for (const inv of bet.invites || []) {
    if (inv.status !== "Accepted") continue;
    const u = typeof inv.user === "object" ? inv.user : null;
    const uid = u?._id || inv.user;
    if (sameUserId(uid, owner._id)) continue;
    push(uid, u?.name || "Player", P2P_TERMS.rosterRoleInvite, inv.acceptedAt, 2);
  }

  return entries.sort((a, b) => {
    if (a.order !== b.order) return a.order - b.order;
    const ta = a.at ? new Date(a.at).getTime() : 0;
    const tb = b.at ? new Date(b.at).getTime() : 0;
    return ta - tb;
  });
};

export const getP2PFilterHint = (filter, favoriteTeam) => {
  const hints = {
    All: "",
    Active: "Open before kickoff — bet against or manage your backing.",
    Pending: "In play or awaiting final result.",
    Settled: "Finished and paid out.",
    Voided: "Cancelled or voided by rules.",
    Created: "Wagers you created.",
    Joined: "Wagers you joined or accepted.",
    Invited: "Private invites waiting on you.",
    Following: "Open offers from people you follow.",
    Favorites: favoriteTeam?.name
      ? `Matches featuring ${favoriteTeam.name}.`
      : "Set a favorite team in Profile to use this filter.",
  };
  return hints[filter] || "";
};

const normalizeTeamName = (name) => String(name || "").trim().toLowerCase();

export const getBetCreatorId = (bet) => {
  const owner = bet?.user;
  if (!owner) return null;
  return typeof owner === "object" ? owner._id : owner;
};

export const teamMatchesFavorite = (team, favoriteTeam) => {
  if (!team || !favoriteTeam) return false;
  const hasFavorite =
    favoriteTeam.entityId ||
    favoriteTeam.apiId != null ||
    favoriteTeam.name;
  if (!hasFavorite) return false;

  const favId = favoriteTeam.entityId || favoriteTeam._id;
  const teamId = team._id || team.id;
  if (favId && teamId && String(favId) === String(teamId)) return true;

  const favApiId = favoriteTeam.apiId;
  const teamApiId = team.apiId ?? team.team?.id;
  if (
    favApiId != null &&
    teamApiId != null &&
    Number(favApiId) === Number(teamApiId)
  ) {
    return true;
  }

  const favName = normalizeTeamName(favoriteTeam.name);
  const teamName = normalizeTeamName(team.name);
  return Boolean(favName && teamName && favName === teamName);
};

export const fixtureHasFavoriteTeam = (fixture, favoriteTeam) => {
  if (!fixture || !favoriteTeam) return false;
  const home = fixture.teams?.home || fixture.homeTeam || null;
  const away = fixture.teams?.away || fixture.awayTeam || null;
  return (
    teamMatchesFavorite(home, favoriteTeam) ||
    teamMatchesFavorite(away, favoriteTeam)
  );
};

export const isBetFromFollowedCreator = (bet, followingIds) => {
  if (!Array.isArray(followingIds) || !followingIds.length) return false;
  const creatorId = getBetCreatorId(bet);
  if (!creatorId) return false;
  const key = String(creatorId);
  return followingIds.some((id) => String(id) === key);
};
